import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Typography,
  Alert,
  Grid,
} from "@mui/material";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { useCreatePlanSubscriptionMutation } from "../../store/userApi";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";

interface SubscriptionFormProps {
  planId: string;
  planName: string;
  planPrice: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface BillingAddress {
  name: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

const steps = ["Billing Address", "Payment Method", "Review"];

export const SubscriptionForm: React.FC<SubscriptionFormProps> = ({
  planId,
  planName,
  planPrice,
  onSuccess,
  onCancel,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billingAddress, setBillingAddress] = useState<BillingAddress>({
    name: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
  });

  const stripe = useStripe();
  const elements = useElements();
  const user = useSelector((state: RootState) => state.user.user);
  const [createPlanSubscription] = useCreatePlanSubscriptionMutation();

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setBillingAddress((prev) => ({ ...prev, [name]: value }));
  };

  const validateBillingAddress = () => {
    const requiredFields = ['name', 'line1', 'city', 'state', 'postalCode', 'country'];
    const missingFields = requiredFields.filter(field => !billingAddress[field as keyof BillingAddress]);
    
    if (missingFields.length > 0) {
      setError(`Please fill in the required fields: ${missingFields.join(', ')}`);
      return false;
    }
    
    setError(null);
    return true;
  };

  const handleNext = () => {
    if (activeStep === 0 && !validateBillingAddress()) {
      return;
    }
    
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError(null);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      setError("Stripe has not been initialized.");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError("Card element not found.");
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: billingAddress.name,
          address: {
            line1: billingAddress.line1,
            line2: billingAddress.line2 || undefined,
            city: billingAddress.city,
            state: billingAddress.state,
            postal_code: billingAddress.postalCode,
            country: billingAddress.country,
          },
        },
      });

      if (stripeError) {
        setError(`Payment error: ${stripeError.message}`);
        setIsProcessing(false);
        return;
      }

      if (!paymentMethod.id) {
        setError("Failed to create payment method.");
        setIsProcessing(false);
        return;
      }

      // Extract first and last name from the billing address name field
      const nameParts = billingAddress.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const response = await createPlanSubscription({
        paymentMethodId: paymentMethod.id,
        planId: planId,
        customerName: {
          firstName,
          lastName
        },
        billingAddress: {
          line1: billingAddress.line1,
          line2: billingAddress.line2 || "",
          city: billingAddress.city,
          state: billingAddress.state,
          postal_code: billingAddress.postalCode,
          country: billingAddress.country
        }
      }).unwrap();

      if (response) {
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setError("Subscription creation failed.");
      }
    } catch (err: any) {
      setError(`Subscription error: ${err.message || "Unknown error occurred"}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Full Name"
                name="name"
                value={billingAddress.name}
                onChange={handleAddressChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Address Line 1"
                name="line1"
                value={billingAddress.line1}
                onChange={handleAddressChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                name="line2"
                value={billingAddress.line2}
                onChange={handleAddressChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="City"
                name="city"
                value={billingAddress.city}
                onChange={handleAddressChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="State/Province"
                name="state"
                value={billingAddress.state}
                onChange={handleAddressChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Postal Code"
                name="postalCode"
                value={billingAddress.postalCode}
                onChange={handleAddressChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Country"
                name="country"
                value={billingAddress.country}
                onChange={handleAddressChange}
              />
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Card Information
            </Typography>
            <Box sx={{ p: 2, border: "1px solid #ddd", borderRadius: 1 }}>
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#424770",
                      "::placeholder": {
                        color: "#aab7c4",
                      },
                    },
                    invalid: {
                      color: "#9e2146",
                    },
                  },
                }}
              />
            </Box>
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Subscription Summary
            </Typography>
            <Box sx={{ my: 2 }}>
              <Typography variant="subtitle1">Plan: {planName}</Typography>
              <Typography variant="subtitle1">Price: ${planPrice}/month</Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 2 }}>
              By clicking "Subscribe", you agree to our Terms of Service and authorize us to charge your payment method
              on a recurring basis. You can cancel anytime.
            </Typography>
          </Box>
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 600, mx: "auto" }}>
      <Typography variant="h5" align="center" gutterBottom>
        Subscribe to {planName}
      </Typography>
      
      <Stepper activeStep={activeStep} sx={{ mb: 4, pt: 2 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {getStepContent(activeStep)}
      
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
        <Button 
          variant="outlined" 
          onClick={activeStep === 0 ? handleCancel : handleBack}
          disabled={isProcessing}
        >
          {activeStep === 0 ? "Cancel" : "Back"}
        </Button>
        <Button
          variant="contained"
          onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <CircularProgress size={24} />
          ) : activeStep === steps.length - 1 ? (
            "Subscribe"
          ) : (
            "Next"
          )}
        </Button>
      </Box>
    </Paper>
  );
};